import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, ArrowLeft } from 'lucide-react';
import { blogApi } from '../services/api';

const BlogPage = () => {
  const { t } = useTranslation();
  const { isRTL, currentLang } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  useEffect(() => {
    document.title = currentLang === 'ar'
      ? 'المدونة | أرمان كيتشنز'
      : 'Blog | Arman Kitchens';
  }, [currentLang]);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await blogApi.getAll();
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(currentLang === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <main data-testid="blog-page" className="pt-24">
      {/* Hero Banner */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1527501034545-bbadafbf308b)' }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="label-text mb-4 block">{t('blog.label')}</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              {t('blog.title')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-4">
                  <div className="aspect-[16/10] bg-muted animate-pulse" />
                  <div className="h-4 bg-muted animate-pulse w-1/3" />
                  <div className="h-6 bg-muted animate-pulse" />
                  <div className="h-4 bg-muted animate-pulse" />
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">
                {currentLang === 'ar' ? 'لا توجد مقالات حالياً' : 'No blog posts yet'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`blog-post-${post.id}`}
                  className="group"
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="aspect-[16/10] overflow-hidden mb-4">
                      <img
                        src={post.cover_image || 'https://images.unsplash.com/photo-1527501034545-bbadafbf308b'}
                        alt={currentLang === 'ar' ? post.title_ar : post.title_en}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(post.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User size={14} />
                        {post.author}
                      </span>
                    </div>

                    <h2 className="text-xl font-semibold text-foreground group-hover:text-gold transition-colors mb-2">
                      {currentLang === 'ar' ? post.title_ar : post.title_en}
                    </h2>
                    
                    <p className="text-muted-foreground line-clamp-2 mb-4">
                      {currentLang === 'ar' ? post.excerpt_ar : post.excerpt_en}
                    </p>

                    <span className="inline-flex items-center gap-2 text-gold text-sm font-medium group-hover:gap-3 transition-all">
                      {t('blog.readMore')}
                      <Arrow size={16} />
                    </span>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default BlogPage;
